import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from '@dcp/shared'
import { Loader2, Tag } from 'lucide-react'
import { useCreateCategory, useUpdateCategory } from '@/hooks/useCategories'
import type { Category } from '@/lib/supabase'
import {
  Modal,
  Button,
  Input,
  Label,
} from '@atrvd/saas-ui'

/**
 * Form validation schema for category creation/editing
 */
const categoryFormSchema = z.object({
  name: z.string().min(1, 'Category name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional().transform(val => val === '' ? undefined : val),
  sage_gl_account: z.string().max(50, 'GL account must be less than 50 characters').optional().transform(val => val === '' ? undefined : val),
  is_active: z.boolean().default(true),
  default_priority: z.boolean().default(false),
})

type CategoryFormData = z.infer<typeof categoryFormSchema>

/**
 * Props for CategoryFormDialog component
 */
interface CategoryFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: Category | null
}

/**
 * Dialog form for creating or editing categories
 */
export function CategoryFormDialog({ open, onOpenChange, category }: CategoryFormDialogProps) {
  const isEditing = !!category
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: '',
      description: '',
      sage_gl_account: '',
      is_active: true,
      default_priority: false,
    },
  })

  /**
   * Reset form when dialog opens/closes or category changes
   */
  useEffect(() => {
    if (open) {
      if (category) {
        // Editing existing category
        reset({
          name: category.name,
          description: category.description || '',
          sage_gl_account: category.sage_gl_account || '',
          is_active: category.is_active,
          default_priority: category.default_priority ?? false,
        })
      } else {
        // Creating new category
        reset({
          name: '',
          description: '',
          sage_gl_account: '',
          is_active: true,
          default_priority: false,
        })
      }
    }
  }, [open, category, reset])

  /**
   * Handle form submission
   */
  const onSubmit = async (data: CategoryFormData) => {
    try {
      if (isEditing) {
        await updateCategory.mutateAsync({
          id: category.id,
          name: data.name,
          description: data.description || null,
          sage_gl_account: data.sage_gl_account || null,
          is_active: data.is_active,
          default_priority: data.default_priority,
        })
      } else {
        await createCategory.mutateAsync({
          name: data.name,
          description: data.description || null,
          sage_gl_account: data.sage_gl_account || null,
          default_priority: data.default_priority,
        })
      }

      handleClose()
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  }

  /**
   * Handle closing the dialog
   */
  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  const isPending = createCategory.isPending || updateCategory.isPending

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={isEditing ? 'Edit Category' : 'Add Category'}
      description={
        isEditing
          ? 'Update the details of this category'
          : 'Create a new category to organize your transactions'
      }
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            onClick={handleSubmit(onSubmit)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <Tag className="mr-2 h-4 w-4" />
                {isEditing ? 'Save Changes' : 'Create Category'}
              </>
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Category Name */}
        <div className="space-y-2">
          <Label htmlFor="name">
            Category Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder="e.g., Equipment Purchase, Resident Fees"
            {...register('name')}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <textarea
            id="description"
            placeholder="Optional description for this category"
            rows={3}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description.message}</p>
          )}
        </div>

        {/* Sage GL Account */}
        <div className="space-y-2">
          <Label htmlFor="sage_gl_account">Sage GL Account</Label>
          <Input
            id="sage_gl_account"
            placeholder="e.g., 4000"
            {...register('sage_gl_account')}
          />
          {errors.sage_gl_account && (
            <p className="text-sm text-destructive">{errors.sage_gl_account.message}</p>
          )}
        </div>

        {/* Default Priority */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="default_priority"
            className="h-4 w-4 rounded border-gray-300"
            {...register('default_priority')}
          />
          <div>
            <Label htmlFor="default_priority" className="text-sm font-normal">
              Default Priority
            </Label>
            <p className="text-xs text-muted-foreground">
              Projections will be marked as priority by default
            </p>
          </div>
        </div>

        {/* Active Status (only show when editing) */}
        {isEditing && (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_active"
              className="h-4 w-4 rounded border-gray-300"
              {...register('is_active')}
            />
            <Label htmlFor="is_active" className="text-sm font-normal">
              Active (inactive categories won't be available for new transactions)
            </Label>
          </div>
        )}

        <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
          <p>
            Categories help organize your transactions.
          </p>
        </div>
      </form>
    </Modal>
  )
}
